<template lang="pug">
#other-user-opinion-message-detail
  // title
  .d-flex.flex-row.justify-content-center.mb-1
    .page-title(v-if="state")
      span(v-if="state.functionContents && state.functionContents.hidden") 匿名からの連絡と回答履歴
      span(v-else) {{ state.groupMemberName }}様からの連絡と回答履歴
  .mb-3.separator

  // status
  .d-flex.align-items-center.justify-content-center.mb-2
    ConfirmStatusSwitch(v-model="state.functionContents.isFinished" @onSwitch="onChangeStatus(state)")

  .card.mb-4
    // opinion messages
    .lene-contents.d-flex.flex-column(v-BottomScroll)
      .mt-2(v-for="message in messages")
        .mb-1(:class="{ 'line-left': message.senderType == 0, 'line-right': message.senderType == 1 }")
          .line-message
            template(v-for="content in createSendMessageContents(message)")
              .text-content.pre-line(v-if="content.type == 'text'")
                span {{ content.value }}
              .link-content.text-center(v-else-if="content.type == 'link'" style="padding: 4px;")
                a(:href="content.uri" target="_blank") {{ content.value }}
              .image-content(v-else-if="content.type == 'image'")
                .image
                  img(:src="content.url")
              .pdf-content(v-else-if="content.type == 'pdf'")
                .pdf.flex.flex-send-message
                  .title.pre-line 添付ファイル
                  .pre-line
                    span PDFの添付ファイルがあります。
                    br
                    span ファイル名：{{ content.fileName }}
                  .link
                    a.pdf-download(:href="content.url" download="HitomeQコネクト-受信メッセージ-添付ファイル.pdf" target="_blank") PDFファイルを開く
          .datetime {{ getDisplayDate(message) }}

    // input reply message
    .opinion-reply-contents.d-flex.flex-column.align-items-center
      // text input
      .p-2(style="width: 100%;")
        .mb-1
          span.key-name 返信内容
        .mb-3
          b-form-textarea(v-model="text" name="reply-message" placeholder="返信内容を入力" rows="10" max-rows="10" maxlength="1000"
            @focus="hideFooter" @blur="showFooter" v-validate="{ required: true, max: 1000 }" aria-describedby="text-live-feedback" data-vv-as="返信内容"
            :class="{ 'disable': !_canReply }" :disabled="!_canReply")
        // file input
        template(v-if="1 <= planType")
          .mt-2.mb-1
            span.key-name 添付ファイル
            span.comment ※最大4ファイル
          .form-input.mb-3
            b-form-file(v-model="files" name="files" :placeholder="_filePlaceholder" browse-text="画像を選択" accept="image/jpeg, image/png"
              :class="{ 'disable': !_canReply }" :disabled="!_canReply" multiple)
              template(#file-name="{ names }")
                span {{ files.length }}ファイル選択
            .error-messages.mt-1(v-if="errors.has('files')")
              label.error-message(v-for="error in errors.collect('files')") {{ error }}
        // reply button
        .mb-3
          ValidationAlert(v-model="showValidationAlert")
        .reply-buttons
          b-button.m-btn(@click="onReply" :class="{ 'disable': !_canReply }" :disabled="!_canReply") 確認画面へ
    .error-messages.pr-2.pb-2.pl-2(v-if="existMember == false")
      label.error-message.m-0 ユーザーが削除されているため返信できません

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.back-btn(@click="onBack") 戻る

  // loading modal
  Loading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileOtherUserReplyReceiveMessageStore } from "@/store/store";
import { MobileOtherUserReceiveMessageState } from "@/store/MobileOtherUserReceiveMessage";

import Setting from "../../../../public/setting.json";

import { Url } from "@/commons/constants/Url";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import SystemError from "@/commons/models/SystemError";
import { PlanType } from "@/commons/services/Plans";
import ImageFileCompression from "@/commons/utils/ImageFileCompression";

import OtherUserOpinionMessagesService, { OtherUserOpinionMessageGetModel } from "@/commons/services/OtherUserOpinionMessages";
import OtherUserGroupMessageModel from "@/commons/models/OtherUserGroupMessage";
import { OpinionFunction } from "@/commons/models/Message";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import S3UrlService from "@/commons/services/S3Url";

import Loading from "@/mobile/basics/Loading.vue";
import ValidationAlert from "@/mobile/basics/ValidationAlertByValidator.vue";
import ConfirmStatusSwitch from "@/mobile/basics/ConfirmStatusSwitch.vue";
import BottomScroll from "@/pc/basics/BottomScroll";

/**
 * ### 受信メッセージ詳細(施設関係者)コンポーネント
 */
@Component({
  components: {
    Loading,
    ValidationAlert,
    ConfirmStatusSwitch
  },
  directives: {
    BottomScroll
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherUserOpinionMessageDetail extends Vue {
  /** 意見メッセージサービス */
  @Inject("OtherUserOpinionMessagesService") otherUserOpinionMessagesService!: OtherUserOpinionMessagesService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ### 新規送信入力画面での入力情報 */
  @Getter("mobileOtherUserReceiveMessageState") state!: MobileOtherUserReceiveMessageState;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = true;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** メンバーが存在するか否か(削除されたユーザーの場合はfalse) */
  existMember: boolean = false;
  /** メッセージのやりとり */
  messages: Array<OtherUserOpinionMessageGetModel> = [];

  /** 返信メッセージ */
  text: string = "";
  /** 添付ファイル */
  files: Array<File> = [];

  get _canReply(): boolean {
    if (this.messages.length == 0 || !this.state || (this.state.functionContents && this.state.functionContents.hidden) || this.existMember == false) {
      return false;
    } else {
      return true;
    }
  }

  get _filePlaceholder(): string {
    if (0 < this.files.length) {
      return `${this.files.length}ファイル選択`;
    } else {
      return "";
    }
  }

  /**
   * ### vue lifecycle created
   *
   * - 施設の表示権限で「受信メッセージ(opinion)」がfalseの場合は404画面へ遷移する
   */
  public created(): void {
    const opinionPermitted: boolean = userStore.facility.permittedFunctions["otherUserOpinion"];
    if (opinionPermitted == false) {
      this.$router.push(Url.NotFound);
    }
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 家族と施設のメッセージのやりとりを取得する
   */
  public async createView(): Promise<void> {
    this.loadingMessage = "読み込み中";
    this.isBusy = true;

    const member: OtherUserGroupModel | null = await this.getOtherUserGroupMember(this.state.groupId, this.state.groupMemberId);
    this.existMember = member != null ? true : false;

    const messages: Array<OtherUserOpinionMessageGetModel> = await this.getOpinionMessage(this.state);
    await this.convertTempFiles(messages);
    this.messages = messages;

    this.planType = userStore.facility.planType;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループメンバーを取得する
   *
   * - 施設関係者グループメンバー単一を取得するAPIはないので、施設関係者グループにひもづくメンバーを取得する
   * - 施設関係者グループメンバーの中からメンバーIDが同一のものを修正対象のメンバーと判断する
   * - 見つからなかった場合は404画面に遷移させたいので例外を投げる
   */
  public async getOtherUserGroupMember(groupId: number, memberId: number): Promise<OtherUserGroupModel | null> {
    const members: Array<OtherUserGroupModel> = await this.otherUserGroupMembersService.get(userStore, groupId);
    for (const member of members) {
      if (member.memberId == memberId) {
        return member;
      }
    }
    return null;
  }

  /**
   * ### 家族と施設のメッセージのやりとりを取得する
   */
  public async getOpinionMessage(opinion: MobileOtherUserReceiveMessageState): Promise<Array<OtherUserOpinionMessageGetModel>> {
    let messages: Array<OtherUserOpinionMessageGetModel> = await this.otherUserOpinionMessagesService.get(userStore, opinion.otherUserOpinionMessagesCaseId);
    return messages;
  }

  /**
   * ### 添付ファイルURL変換
   *
   * - 添付ファイルがある場合はそのURLを変換する
   */
  public async convertTempFiles(messages: Array<OtherUserOpinionMessageGetModel>): Promise<void> {
    for (const message of messages) {
      if (message.attachedFiles) {
        for (const file of message.attachedFiles) {
          if (file.url) {
            const convertedUrl: string = await this.convertS3Url(file.url);
            file.url = convertedUrl;
          }
          // URLが存在しないことはあり得ないのでシステムエラー
          else {
            throw new SystemError("attachedFiles.type is invalid. sortKey = " + message.otherUserOpinionMessagesCaseId);
          }
        }
      }
    }
    return;
  }

  /**
   * ### LINEメッセージ情報のメッセージ種別から判断して、画面表示用のメッセージを作成する
   */
  private async convertS3Url(url: string): Promise<string> {
    // 画像URLが渡されてきた場合はS3にある画像の公開設定を行ってそのURLを画面で使用する
    let publicUrl: string = "";
    try {
      publicUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, url);
    } catch (error) {
      // S3の画像が削除されてしまう場合があり得るためシステムエラーにはしない
      console.error("S3 image not fount");
    }
    return publicUrl;
  }

  /**
   * ### コンテンツを作成する
   */
  public createSendMessageContents(message: OtherUserOpinionMessageGetModel): Array<{ type: string; value: string; uri?: string; url?: string; fileName?: string }> {
    // contentが存在しない場合は空配列を返す
    if (!message.content) {
      return [];
    }

    // template側でlinkボタンとテキストを判別するためにtypeを持たせる
    let contents: Array<{ type: string; value: string; uri?: string; url?: string; fileName?: string }> = [];

    const splitedContent: string[] = message.content.split("\n");
    let buttonCount: number = 1;

    for (const message of splitedContent) {
      // リンクボタンメッセージ(ボタン名指定)
      // 先頭に半角全角スペースがあるだけの場合は無視して扱い、それ以外はテキスト文字列として扱う
      if (message.match(/^\[(.*)\]\((.*)\)$/)) {
        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const links: Array<{ name: string; url: string }> = [];
        let match: any;

        while ((match = linkRegex.exec(message)) !== null) {
          const name = match[1] ? match[1] : null;
          const url = match[2] ? match[2] : null;
          links.push({ name, url });
        }

        if (links.length == 1 && links[0].name != null && links[0].url != null) {
          contents.push({
            type: "link",
            value: `${links[0].name}`,
            uri: links[0].url
          });
        } else {
          contents.push({
            type: "text",
            value: message
          });
        }
      }
      // リンクボタンメッセージ(ボタン名指定無し)
      // 先頭に半角全角スペースがあるだけの場合はURLとして扱い、それ以外はテキスト文字列として扱う
      else if (message.match(/^\s*(http|https):\/\/.+/)) {
        // 前後の半角全角スペースを取り除き、URLデコード&エンコードを考慮したURIを作成する
        const uri: string = message.replace(/^\s+|\s+$/g, "");
        const decodedUri: string = decodeURI(uri);
        const encodedUri: string = encodeURI(decodedUri);

        buttonCount++;
        contents.push({
          type: "link",
          value: `リンク その${buttonCount}`,
          uri: encodedUri
        });
      }
      // テキストメッセージ
      else {
        const convertedMessage: string = message == "" ? "　" : message;
        contents.push({
          type: "text",
          value: convertedMessage
        });
      }
    }

    // 添付ファイルがある場合は添付ファイルもコンテンツとする
    if (message.attachedFiles) {
      for (const file of message.attachedFiles) {
        if (file.type == "image") {
          contents.push({
            type: file.type,
            value: "",
            url: file.url,
            fileName: file.name
          });
        } else if (file.type == "pdf") {
          contents.push({
            type: file.type,
            value: "",
            url: file.url,
            fileName: file.name
          });
        }
      }
    }

    return contents;
  }

  /**
   * ### 画面表示用の日付を返す
   */
  public getDisplayDate(message: OtherUserOpinionMessageGetModel): string {
    if (message.datetime) {
      return this.$dateUtil.convertNumberToString(Number(message.datetime), "mm月dd日 hh:MM");
    } else {
      return "";
    }
  }

  /**
   * ### フッターを表示する
   */
  public showFooter(): void {
    this.$emit("updateShowFooter", true);
    return;
  }

  /**
   * ### フッターを非表示にする
   */
  public hideFooter(): void {
    this.$emit("updateShowFooter", false);
    return;
  }

  /**
   * ### 意見の状態を更新する
   *
   * - 意見の状態を更新する
   * - サイドメニューの未完了バッジを更新する
   */
  public async onChangeStatus(opinion: OtherUserGroupMessageModel): Promise<void> {
    this.loadingMessage = "更新中";
    this.isBusy = true;

    const updateOpinion: OtherUserGroupMessageModel = this.createUpdateParam(opinion);
    await this.otherUserOpinionMessagesService.patch(userStore, opinion.otherUserOpinionMessagesCaseId, updateOpinion);

    this.$emit("refresh");

    this.isBusy = false;

    return;
  }

  /**
   * ### 意見更新パラメータを作成する
   *
   * - 意見状態の確認はfinishedに日時が格納されることと同義なので、finishedに日時を入れて更新する
   * - 意見状態を未確認にする場合は0を格納してAPIに渡すIFになっている
   */
  public createUpdateParam(opinion: OtherUserGroupMessageModel): OtherUserGroupMessageModel {
    const _functionContents: any = opinion.functionContents;
    const contents: OpinionFunction = _functionContents;

    // データ破損でnullが格納されている場合があるため、チェックして空文字に置き換える
    contents.content = contents.content ? contents.content : "";

    if (!contents.finished) {
      const date: Date = new Date();
      contents.finished = this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMMss000");
      contents.isFinished = true;
    } else {
      contents.finished = 0;
      contents.isFinished = false;
    }

    opinion.writtenBy = userStore.loginUser.name;
    opinion.writerId = userStore.loginUser.userId;

    return opinion;
  }

  /**
   * ### 送信ボタン押下イベント
   *
   * - 返信内容をバリデーションチェックする
   * - ファイルが画像の場合、圧縮を行う
   * - 次画面で使用する情報をstoreに格納する
   * - 前ページからの情報を引き継いで次画面にも渡しつつ返信内容確認画面へ遷移する
   */
  public async onReply(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const fileValidationResult: boolean = this.fileValidate(this.files);
    if (validationResult == true && fileValidationResult == true) {
      this.showValidationAlert = false;
      const sendTargetFiles: Array<File> = [];
      for (const file of this.files) {
        if (file.type == "image/jpeg" || file.type == "image/png") {
          const compressedFile: File | undefined = await ImageFileCompression.imageCompression(file, file.type, 1 * 1024 * 1024, 4096, 4096);
          if (compressedFile) {
            sendTargetFiles.push(compressedFile);
          }
        } else {
          sendTargetFiles.push(file);
        }
      }

      mobileOtherUserReplyReceiveMessageStore.updateMobileOtherUserReplyReceiveMessage({
        state: {
          text: this.text,
          files: sendTargetFiles,
          otherUserGroupMemberTel: this.messages[0].groupMemberTel,
          otherUserGroupMemberOrganization: this.messages[0].groupMemberOrganization,
          receiver: this.messages[0].sender,
          exist: true
        }
      });

      const type: string = this.$route.params.type;
      const page: string = this.$route.params.page;
      this.$router.push({ name: MobileUrl.OtherUserReplyReceiveMessageConfirm, params: { type: type, page: page } });
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### ファイルのバリデーション
   *
   * - ファイル入力はバリデーターを使えないので、自前でバリデーションを実施する
   * - ファイル数バリデーション
   * - ファイル種別バリデーション
   */
  public fileValidate(files: Array<File>): boolean {
    for (const file of files) {
      if (!file || !file.name || file.size == 0) {
        this.$validator.errors.add({ field: "files", msg: `ファイルが破損等をしている可能性があり、ファイルを認識することができません。お手数ですが、再度選択し直してください。` });
        return false;
      }
    }
    if (4 < files.length) {
      this.$validator.errors.add({ field: "files", msg: "添付ファイル最大登録ファイル数以上は登録できません" });
      return false;
    }
    for (const file of files) {
      if (file.type != "image/jpeg" && file.type != "image/png") {
        this.$validator.errors.add({ field: "files", msg: `${file.name}はjpg、png以外の拡張子のため登録できません` });
        return false;
      }
    }
    return true;
  }

  /**
   * ### 戻るボタン押下イベント
   */
  public onBack(): void {
    const type: string = this.$route.params.type;
    const page: string = this.$route.params.page;

    if (type == "1") {
      this.$router.push({ name: MobileUrl.OtherUserReceiveMessageList, params: { page: page } });
    } else if (type == "2") {
      this.$router.push(MobileUrl.NewArrivalMessagesList);
    } else {
      this.$router.push(MobileUrl.Home);
    }

    return;
  }
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#other-user-opinion-message-detail {
  .lene-contents {
    overflow-y: auto;
    height: calc(30vh);
    background-color: $bg-message;

    .line-left,
    .line-right {
      position: relative;
      clear: both;

      .line-message {
        padding: 10px;
        border-radius: 20px;
        margin: 0;
        // LINEの吹き出しのように見せるために、CSSで図形を描画
        &::after {
          content: "";
          position: absolute;
          display: block;
          width: 0;
          height: 0;
          top: 10px;
          border-top: 10px solid transparent;
          border-bottom: 10px solid transparent;
        }
      }
      .datetime {
        color: $font-date;
        font-size: 0.9rem;
        font-weight: bold;
      }
      // LINE風に表示を行うため、vueがメッセージの情報を取得後、スクロールバーの位置を一番下に移動させている
      // vueがDOM要素を作成した後、img要素のsrc属性のURLにGETが走り、画像が取得される
      // 画像の大きさを固定しておかなかった場合、img要素は動的に高さが変わり、スクロールバーの長さが変わる
      // スクロールバーの長さが変わると、スクロール位置もずれてしまい、位置が一番下ではなくなってしまう
      // これを回避するため、画像を表示するdiv要素の高さおよびimg要素の高さを固定し、img要素の高さが動的に変わるのを防ぐ
      .image {
        // 「ここで高さを固定しないと、問題が発生するため、高さ固定」とあるが、max-heightで問題がないか確認する
        // height: 320px;
        max-height: 320px;
        img {
          // 画像サイズは高さ固定
          height: 300px;
          max-width: 700px;
        }
      }
      .link {
        padding: 8px;
        cursor: pointer;
        a {
          color: $font-key;
        }
      }
    }

    // 家族の発言
    .line-left {
      display: inline-block;
      margin-bottom: 5px;
      margin-left: 30px;
      .line-message {
        background-color: $bg-message-bot;
        margin-right: 80px;
        &::after {
          left: -10px;
          border-right: 20px solid $bg-message-bot;
        }
      }
      .datetime {
        text-align: left;
      }
    }
    // 自分の発言
    .line-right {
      float: right;
      margin-top: 5px;
      margin-right: 30px;
      .line-message {
        background-color: $bg-message-mine;
        color: rgba(255, 255, 255, 1);
        margin-left: 80px;
        // LINEの吹き出しのように見せるために、CSSで図形を描画
        &::after {
          right: -10px;
          border-left: 20px solid $bg-message-mine;
        }
      }
      .datetime {
        text-align: right;
      }
    }
  }

  .reply-buttons {
    text-align: center;
    min-width: 54px;
  }
  .back-btn {
    min-width: 84px;
  }
}
</style>
